import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { devtools } from 'zustand/middleware';
import type { BulkSearch } from '../types/search';

/**
 * The live search driving the search-results layer.
 *
 * The nav search input and the advanced-search builder stay Angular through Wave 3, so
 * this store is fed by the Angular events they broadcast (see SearchResultsPage) rather
 * than by React state. Two independent signals arrive, in either order:
 *
 * - `search` — the executed query (Search.SearchExecuted).
 * - `collectionId` — the search scope (SearchContext.Changed), null = project-wide.
 *
 * Both are latest-wins state, never triggers: a context change re-executes the search
 * by broadcasting Search.SearchExecuted after it (develop's fix/37609), so the page
 * rebuilds its datasource from whatever the two slots hold at that moment.
 */
interface SearchState {
  search: BulkSearch | null;
  collectionId: string | null;

  setSearch: (search: BulkSearch) => void;
  setCollectionId: (collectionId: string | null) => void;
  clear: () => void;
}

export const useSearchStore = create<SearchState>()(
  devtools(
    immer((set) => ({
      search: null,
      collectionId: null,

      setSearch: (search) =>
        set((s) => {
          s.search = search;
        }),

      setCollectionId: (collectionId) =>
        set((s) => {
          s.collectionId = collectionId;
        }),

      clear: () =>
        set((s) => {
          s.search = null;
          s.collectionId = null;
        }),
    })),
    { name: 'searchStore' }
  )
);
